import React, { useState, useEffect } from "react";
import { Modal, Button, Select, Table, Radio, Form, List, Checkbox } from "antd";
import { useConstants } from "../context/ConstantsContext";
import { fetchWeatherStations, fetchEventLocations, updateEventLocations } from "../api";

const { Option } = Select;

const EditEventModal = ({ open, onClose, event, message }) => {
  const constants = useConstants();
  const [sortedStations, setSortedStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [eventLocations, setEventLocations] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [alternateEnabled, setAlternateEnabled] = useState({});
  const [newlyAddedStations, setNewlyAddedStations] = useState(new Set());

  useEffect(() => {
    const loadWeatherStations = async () => {
      setLoading(true);
      try {
        const weatherStations = await fetchWeatherStations();
        setSortedStations(sortWeatherStations(weatherStations));
      } catch (error) {
        message.error('Failed to fetch weather stations');
        console.error('Error fetching weather stations:', error);
      } finally {
        setLoading(false);
      }
    }

    loadWeatherStations();
  }, [open, message]);

  useEffect(() => {
    if (open) {
      setEventLocations(event?.locations || []);
      setForecasts(event?.forecasts || []);
      setNewlyAddedStations(new Set()); // Reset newly added stations when modal opens
    }
  }, [open, event]);

  useEffect(() => {
    if (forecasts.length > 0) {
      const initialValues = {};
      const initialAlternateEnabled = {};
  
      forecasts.forEach((forecast) => {
        const hasAlternate = forecast.alternate_forecast && forecast.alternate_forecast !== forecast.forecast_id;
        initialValues[`${forecast.weather_station_id}_pdf_source`] = forecast.pdf_source || null;
        initialValues[`${forecast.weather_station_id}_pdf_time`] = forecast.pdf_time || null;
        initialValues[`${forecast.weather_station_id}_pdf_type`] = forecast.pdf_type || null;
        // Only set alternate_forecast if it's actually an alternate (not self-reference)
        initialValues[`${forecast.weather_station_id}_alternate_forecast`] = hasAlternate ? forecast.alternate_forecast : null;
        initialAlternateEnabled[forecast.weather_station_id] = hasAlternate;
      });
  
      form.setFieldsValue(initialValues);
      setAlternateEnabled(initialAlternateEnabled);
    }
  }, [forecasts, form]);


  const sortWeatherStations = (stations) => {
    return [...stations].sort((a, b) => {
      if (a.country !== b.country) return a.country.localeCompare(b.country);
      if (a.province_state !== b.province_state)
        return a.province_state.localeCompare(b.province_state);
      return a.station_name.localeCompare(b.station_name);
    });
  };

  const handleAddStation = async () => {
    if (selectedStation) {
      setLoading(true);
      try {
        const updatedData = await fetchEventLocations(event.event_id, [selectedStation]);
        console.log(updatedData);
  
        const newLocations = updatedData.locations.filter(
          (location) =>
            !eventLocations.some(
              (existingLocation) =>
                existingLocation.weather_station_id === location.weather_station_id
            )
        );
  
        const newForecasts = updatedData.forecasts.filter(
          (forecast) =>
            !forecasts.some(
              (existingForecast) =>
                existingForecast.weather_station_id === forecast.weather_station_id
            )
        );
  
        // Set default form values for newly added forecasts
        const newFormValues = {};
        newForecasts.forEach((forecast) => {
          // No defaults - user must select everything
          newFormValues[`${forecast.weather_station_id}_pdf_source`] = null;
          newFormValues[`${forecast.weather_station_id}_pdf_time`] = null;
          newFormValues[`${forecast.weather_station_id}_pdf_type`] = null;
          newFormValues[`${forecast.weather_station_id}_alternate_forecast`] = null;
        });
        
        setEventLocations((prev) => sortWeatherStations([...prev, ...newLocations]));
        setForecasts((prev) => {
          const updated = sortWeatherStations([...prev, ...newForecasts]);
          // Set form values after state is ready
          setTimeout(() => {
            form.setFieldsValue(newFormValues);
            // Set all new forecasts to not use alternate
            setAlternateEnabled(prev => {
              const newState = { ...prev };
              newForecasts.forEach(f => {
                newState[f.weather_station_id] = false;
              });
              return newState;
            });
            // Track newly added stations
            setNewlyAddedStations(prev => {
              const newSet = new Set(prev);
              newForecasts.forEach(f => newSet.add(f.weather_station_id));
              return newSet;
            });
          }, 0);
          return updated;
        });
  
        setSelectedStation(null);
      } catch (error) {
        message.error("Failed to fetch associated locations. Please try again.");
        console.error("Error adding station:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = await form.validateFields();

      const updatedLocations = eventLocations.map((location) => ({
        weather_station_id: location.weather_station_id
      }));
  
      const updatedForecasts = forecasts.map((forecast) => {
        const useAlternate = alternateEnabled[forecast.weather_station_id] || false;
        return {
          weather_station_id: forecast.weather_station_id,
          pdf_source: formData[`${forecast.weather_station_id}_pdf_source`],
          pdf_time: formData[`${forecast.weather_station_id}_pdf_time`],
          pdf_type: formData[`${forecast.weather_station_id}_pdf_type`],
          alternate_forecast: useAlternate ? formData[`${forecast.weather_station_id}_alternate_forecast`] : forecast.forecast_id,
        };
      });
  
      const result = await updateEventLocations(event.event_id, updatedLocations, updatedForecasts);
      
      // Check if backend returned any warnings about PDFs
      if (result?.warnings && result.warnings.length > 0) {
        message.warning(`Event saved but some forecast PDFs could not be synced: ${result.warnings.join(', ')}`);
      } else {
        message.success("Event locations and forecasts updated successfully!");
      }
      
      onClose();
    } catch (error) {
      message.error("Failed to save changes. Please try again.");
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (stationId, field, value) => {
    setForecasts((prev) =>
      prev.map((forecast) =>
        forecast.weather_station_id === stationId
          ? { ...forecast, [field]: value }
          : forecast
      )
    );
  };

  const forecastColumns = [
    {
      title: "Location",
      dataIndex: "station_name",
      key: "station_name",
      render: (_, record) => `${record.station_name}, ${record.province_state || ""}`,
    },
    {
      title: "Forecast Source",
      dataIndex: "pdf_source",
      key: "pdf_source",
      render: (_, record) => (
        <Form.Item
          name={`${record.weather_station_id}_pdf_source`}
          rules={[{ required: true, message: "Please select a forecast source" }]}
          style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}
        >
          <Radio.Group
            onChange={(e) =>
              handleFieldChange(record.weather_station_id, "pdf_source", e.target.value)
            }
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            {record.country === constants.country.canada && (
              <Radio value={constants.forecast_source.ec}>
                {constants.forecast_source.ec}
              </Radio>
            )}
            {record.country === constants.country.usa && (
              <Radio value={constants.forecast_source.nws}>
                {constants.forecast_source.nws}
              </Radio>
            )}
            <Radio value={constants.forecast_source.wn}>
              {constants.forecast_source.wn}
            </Radio>
          </Radio.Group>
        </Form.Item>
      ),
    },
    {
      title: "Forecast Time",
      dataIndex: "pdf_time",
      key: "pdf_time",
      render: (_, record) => (
        <Form.Item
          name={`${record.weather_station_id}_pdf_time`}
          rules={[{ required: true, message: "Please select a forecast time" }]}
          style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}
        >
          <Radio.Group
            onChange={(e) =>
              handleFieldChange(record.weather_station_id, "pdf_time", e.target.value)
            }
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            <Radio value="8am">8am</Radio>
            <Radio value="11am">11am</Radio>
          </Radio.Group>
        </Form.Item>
      ),
    },
    {
      title: "Forecast Type",
      dataIndex: "pdf_type",
      key: "pdf_type",
      render: (_, record) => {
        const currentPdfSource = form.getFieldValue(`${record.weather_station_id}_pdf_source`) || record.pdf_source;
        return (
          <Form.Item
            name={`${record.weather_station_id}_pdf_type`}
            rules={[{ required: true, message: "Please select a forecast type" }]}
            style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}
          >
            <Radio.Group
              onChange={(e) =>
                handleFieldChange(record.weather_station_id, "pdf_type", e.target.value)
              }
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <Radio value="current">
                {currentPdfSource === constants.forecast_source.wn ? '7-day' : 'Current'}
              </Radio>
              <Radio value="hourly">Hourly</Radio>
            </Radio.Group>
          </Form.Item>
        );
      },
    },
    {
      title: "Use Alternative Forecast",
      dataIndex: "alternate_forecast",
      key: "alternate_forecast",
      render: (_, record) => {
        const isEnabled = alternateEnabled[record.weather_station_id] || false;
        const isNewlyAdded = newlyAddedStations.has(record.weather_station_id);
        const hasAvailableForecasts = forecasts.filter(f => f.pdf_available !== false && f.forecast_id !== record.forecast_id).length > 0;
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Checkbox
              checked={isEnabled}
              disabled={!hasAvailableForecasts || isNewlyAdded}
              onChange={(e) => {
                const checked = e.target.checked;
                console.log('Checkbox changed:', checked, 'for station:', record.weather_station_id);
                
                setAlternateEnabled(prev => ({
                  ...prev,
                  [record.weather_station_id]: checked
                }));
                
                if (checked) {
                  // When enabled, set to null to force user selection
                  form.setFieldsValue({
                    [`${record.weather_station_id}_alternate_forecast`]: null
                  });
                } else {
                  // Reset to null when unchecked
                  form.setFieldsValue({
                    [`${record.weather_station_id}_alternate_forecast`]: null
                  });
                }
              }}
            >
              {isNewlyAdded ? 'Save to enable alternative forecast' : 'Enable Alternative Forecast'}
            </Checkbox>
            
            {isEnabled && hasAvailableForecasts && (
              <Form.Item
                name={`${record.weather_station_id}_alternate_forecast`}
                rules={[{ 
                  required: true, 
                  message: "Please select an alternative forecast" 
                }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select alternative forecast (required)"
                  onChange={(value) => {
                    form.setFieldsValue({
                      [`${record.weather_station_id}_alternate_forecast`]: value
                    });
                    handleFieldChange(record.weather_station_id, "alternate_forecast", value);
                  }}
                  options={forecasts
                    .filter(f => f.pdf_available !== false && f.forecast_id !== record.forecast_id)
                    .map((forecast) => ({
                      value: forecast.forecast_id,
                      label: `${forecast.station_name}, ${forecast.province_state || ''}`
                    }))}
                />
              </Form.Item>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      title="Edit Event Locations"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={loading}
      okText="Save"
      cancelText="Cancel"
      width="80%"
    >
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <Select
          value={selectedStation}
          onChange={(value) => setSelectedStation(value)}
          showSearch
          placeholder="Select a weather station"
          style={{ flex: 1 }}
          optionFilterProp="label"
        >
          {sortedStations.map((station) => (
            <Option
              key={station.weather_station_id}
              value={station.weather_station_id}
              label={`${station.station_name}, ${station.province_state}, ${station.country}`}
            >
              {`${station.station_name}, ${station.province_state}`}
            </Option>
          ))}
        </Select>
        <Button
          type="primary"
          onClick={handleAddStation}
          disabled={!selectedStation}
        >
          Add
        </Button>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <h3>Event Locations</h3>
        <List
          dataSource={eventLocations}
          renderItem={(location) => (
            <List.Item>
              {`${location.station_name}, ${location.province_state || ""}, ${location.country}`}
            </List.Item>
          )}
          bordered
          size="small"
        />
      </div>

      <div>
        <h3>Event Forecasts</h3>
        <Form form={form}>
          <Table
            dataSource={forecasts}
            columns={forecastColumns}
            rowKey="weather_station_id"
            pagination={false}
          />
        </Form>
      </div>
    </Modal>
  );
};

export default EditEventModal;